import React from 'react';
import Logo from '../brand/Logo';
import { CartBagIcon, AccountAvatarIcon } from '../../constants/icons';
import {
    LANDING_URL,
    LANDING_PACKS_URL,
    LANDING_INSTRUMENT_URL,
    LANDING_SUBSCRIBE_URL,
} from '../../constants/urls';

// The single top navbar: a beige band with the logo +
// The Instrument / The Packs / Subscribe on the left, and Cart, an account
// action, and — signed-out only — a Free Download button on the right.
//
// Purely presentational and Router-free. "The Instrument" / "The Packs" are
// always external marketing anchors; Cart, the account action and Subscribe are
// supplied by the caller so the same bar serves both the signed-out auth screens
// (plain anchors, outside the app Router) and the in-app pages (local navigation
// + the cart-count dot). Subscribe falls back to the marketing page when the
// caller doesn't wire it.
//
// An action given as `{ href }` renders an <a>; as `{ onClick }` a <button>;
// omitted entirely (Login on the auth screens) it renders inert text.
export type HeaderAction = { href: string } | { onClick: () => void };

const NAV_LINK = 'text-nav text-label transition-colors hover:text-ink';
const ACTION = 'flex items-center gap-2 text-nav text-ink-secondary transition-colors hover:text-ink';

function HeaderActionEl({
    action,
    className,
    ariaLabel,
    children,
}: {
    action?: HeaderAction;
    className: string;
    ariaLabel?: string;
    children: React.ReactNode;
}) {
    if (action && 'href' in action) {
        return (
            <a href={action.href} aria-label={ariaLabel} className={className}>
                {children}
            </a>
        );
    }
    if (action) {
        return (
            <button type="button" onClick={action.onClick} aria-label={ariaLabel} className={className}>
                {children}
            </button>
        );
    }
    return <span className={className}>{children}</span>;
}

export function SiteHeaderView({
    cart,
    cartHasItems = false,
    account,
    accountLabel,
    subscribe,
    showFreeDownload = false,
}: {
    cart?: HeaderAction;
    cartHasItems?: boolean;
    account?: HeaderAction;
    accountLabel: string; // "Login" signed out, "Account" signed in
    subscribe?: HeaderAction; // omitted → the marketing /subscribe page
    showFreeDownload?: boolean;
}) {
    return (
        <header className="w-full bg-bar">
            <div className="mx-auto flex min-h-[theme(spacing.header)] max-w-[1400px] flex-wrap items-center gap-x-[clamp(20px,4vw,60px)] gap-y-3 px-[clamp(20px,3vw,40px)] py-3">
                <a href={LANDING_URL} aria-label="Organic Electronics Audio — home" className="flex-none">
                    <Logo />
                </a>

                <div className="flex items-center gap-[clamp(20px,4vw,56px)]">
                    <a href={LANDING_INSTRUMENT_URL} className={NAV_LINK}>The Instrument</a>
                    <a href={LANDING_PACKS_URL} className={NAV_LINK}>The Packs</a>
                    {subscribe ? (
                        <HeaderActionEl action={subscribe} className={NAV_LINK}>Subscribe</HeaderActionEl>
                    ) : (
                        <a href={LANDING_SUBSCRIBE_URL} className={NAV_LINK}>Subscribe</a>
                    )}
                </div>

                <nav className="ml-auto flex items-center gap-[clamp(16px,2.5vw,30px)] text-ink-secondary">
                    <HeaderActionEl action={cart} ariaLabel="Cart" className={ACTION}>
                        <CartBagIcon withDot={cartHasItems} />
                        Cart
                    </HeaderActionEl>
                    <HeaderActionEl action={account} ariaLabel={accountLabel} className={ACTION}>
                        <AccountAvatarIcon />
                        {accountLabel}
                    </HeaderActionEl>
                    {showFreeDownload && (
                        <a
                            href={LANDING_URL}
                            className="rounded-btn bg-ink-secondary px-5 py-3 text-ui font-semibold text-white transition hover:opacity-90"
                        >
                            Free Download
                        </a>
                    )}
                </nav>
            </div>
        </header>
    );
}

export default SiteHeaderView;
